import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Bell } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import LoadCard from '../components/LoadCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import NotificationBadge from '../components/NotificationBadge';
import { getLoads } from '../api/loadsApi';
import { useToast } from '../context/ToastContext';
import PullToRefresh from '../components/PullToRefresh';

function Loads() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchLoads = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const result = await getLoads(pageNum, 10);
      if (result.success) {
        setLoads((prev) => (append ? [...prev, ...result.loads] : result.loads));
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || t('common.genericError'));
        showToast('error', result.message || t('common.genericError'));
      }
    } catch (err) {
      setError(t('common.networkError'));
      showToast('error', t('common.networkError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchLoads(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    fetchLoads(page + 1, true);
  };

  const handleRetry = () => {
    fetchLoads(1);
  };

  return (
   <div style={{ minHeight: '100vh', paddingBottom: 'calc(64px + env(safe-area-inset-bottom))', backgroundColor: '#f9fafb' }}>
      <AppHeader
        title={t('loads.title')}
        showLogo={true}
        rightAction={<NotificationBadge onClick={() => navigate('/notifications')} />}
      />
    
	<PullToRefresh onRefresh={() => fetchLoads(1)}>
      <div style={{ padding: 16 }}>
        {loading && <LoadingSpinner fullScreen />}

        {!loading && error && (
          <EmptyState
            icon={Package}
            title={t('common.genericError')}
            message={error}
            onRetry={handleRetry}
            retryLabel={t('common.tryAgain')}
          />
        )}

        {!loading && !error && loads.length === 0 && (
          <EmptyState
            icon={Package}
            title={t('loads.emptyTitle')}
            message={t('loads.emptyMessage')}
          />
        )}

        {!loading && !error && loads.map((load) => (
          <LoadCard key={load.id_load} load={load} />
        ))}

      {!loading && !error && hasMore && (
  <button onClick={handleLoadMore} disabled={loadingMore} style={styles.loadMoreBtn}>
    {loadingMore ? t('common.loading') : t('common.loadMore')}
  </button>
)}
      </div>
	  </PullToRefresh>

      <BottomTabBar />
    </div>
  );
}

const styles = {
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 8,
  },
};

export default Loads;